import { useState, useEffect, useCallback } from "react";
import { AlertTriangle, HelpCircle, Info, X } from "lucide-react";

/**
 * ConfirmDialog — diálogo de confirmação global no estilo Apple/iOS.
 *
 * Uso imperativo (não precisa de hook em cada componente):
 *
 *   import { confirmDialog } from "../components/common/ConfirmDialog";
 *   const ok = await confirmDialog({
 *     title: "Excluir Convidado",
 *     message: "Tem certeza que deseja excluir Gabriel Silva? Esta ação não poderá ser desfeita.",
 *     confirmLabel: "Excluir",
 *     variant: "danger", // "danger" | "confirm" | "info"
 *   });
 *   if (!ok) return;
 *
 * Monte <ConfirmDialogRoot /> uma única vez no topo da árvore (ex.: dentro de App()).
 */

// setter registrado pelo Root — permite chamar fora de componentes React.
let _openConfirm = null;

export function confirmDialog(options = {}) {
  return new Promise((resolve) => {
    if (!_openConfirm) {
      // Fallback seguro: se o Root não estiver montado, não bloqueia a ação.
      resolve(true);
      return;
    }
    _openConfirm(options, resolve);
  });
}

const VARIANTS = {
  danger: {
    icon: AlertTriangle,
    iconWrap: "bg-rose-100 text-rose-600",
    confirmBtn:
      "bg-rose-600 text-white hover:bg-rose-700 active:bg-rose-800 shadow-[0_8px_20px_-6px_rgba(225,29,72,0.5)]",
    ring: "focus-visible:ring-rose-400",
  },
  confirm: {
    icon: HelpCircle,
    iconWrap: "bg-fuchsia-100 text-fuchsia-600",
    confirmBtn:
      "text-white bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:brightness-105 active:brightness-95 shadow-[0_8px_20px_-6px_rgba(192,38,211,0.5)]",
    ring: "focus-visible:ring-fuchsia-400",
  },
  info: {
    icon: Info,
    iconWrap: "bg-sky-100 text-sky-600",
    confirmBtn:
      "text-white bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:brightness-105 active:brightness-95 shadow-[0_8px_20px_-6px_rgba(139,92,246,0.5)]",
    ring: "focus-visible:ring-violet-400",
  },
};

export function ConfirmDialogRoot() {
  const [state, setState] = useState({ open: false, options: {}, resolve: null });

  useEffect(() => {
    _openConfirm = (options, resolve) =>
      setState({ open: true, options: options || {}, resolve });
    return () => {
      _openConfirm = null;
    };
  }, []);

  const close = useCallback(
    (value) => {
      setState((s) => {
        s.resolve?.(value);
        return { ...s, open: false };
      });
    },
    []
  );

  useEffect(() => {
    if (!state.open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close(false);
      if (e.key === "Enter") close(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.open, close]);

  if (!state.open) return null;

  const {
    title = "Confirmar ação",
    message = "",
    confirmLabel,
    cancelLabel = "Cancelar",
    variant = "confirm",
  } = state.options;

  const v = VARIANTS[variant] || VARIANTS.confirm;
  const Icon = v.icon;
  const isInfo = variant === "info";
  const finalConfirmLabel =
    confirmLabel || (isInfo ? "Entendi" : variant === "danger" ? "Excluir" : "Confirmar");

  return (
    <div
      className="fixed inset-0 z-[10090] flex items-center justify-center p-5"
      data-testid="confirm-dialog-overlay"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-zinc-900/40 backdrop-blur-[3px] animate-fadeIn"
        onClick={() => close(false)}
        aria-hidden="true"
      />

      {/* Card iOS */}
      <div
        role="alertdialog"
        aria-modal="true"
        className="relative w-full max-w-[360px] rounded-[28px] bg-white shadow-[0_24px_60px_-12px_rgba(0,0,0,0.35)] overflow-hidden"
        style={{ animation: "confirm-pop 220ms cubic-bezier(0.16,1,0.3,1)" }}
        data-testid="confirm-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => close(false)}
          className="absolute right-3.5 top-3.5 grid h-8 w-8 place-items-center rounded-full text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 transition-colors"
          aria-label="Fechar"
          data-testid="confirm-dialog-close"
        >
          <X className="h-4.5 w-4.5" strokeWidth={2.2} />
        </button>

        <div className="px-7 pt-9 pb-6 text-center">
          <div
            className={`mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full ${v.iconWrap}`}
          >
            <Icon className="h-7 w-7" strokeWidth={2.1} />
          </div>

          <h2 className="text-[19px] font-bold tracking-tight text-zinc-900 leading-snug">
            {title}
          </h2>

          {message ? (
            <p className="mt-2 text-[14.5px] leading-relaxed text-zinc-500 whitespace-pre-line">
              {message}
            </p>
          ) : null}
        </div>

        <div className="px-5 pb-5 flex flex-col gap-2.5">
          <button
            type="button"
            onClick={() => close(true)}
            className={`w-full rounded-2xl py-3.5 text-[15.5px] font-semibold transition-all duration-150 active:scale-[0.98] focus:outline-none focus-visible:ring-2 ${v.confirmBtn} ${v.ring}`}
            data-testid="confirm-dialog-confirm"
            autoFocus
          >
            {finalConfirmLabel}
          </button>

          {!isInfo && (
            <button
              type="button"
              onClick={() => close(false)}
              className="w-full rounded-2xl py-3.5 text-[15.5px] font-semibold text-zinc-600 bg-zinc-100 hover:bg-zinc-200 active:bg-zinc-200 transition-all duration-150 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300"
              data-testid="confirm-dialog-cancel"
            >
              {cancelLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
